import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiRotateCcw, FiTrash2, FiSearch, FiRefreshCw } from 'react-icons/fi';
import { loadTable, saveRow, getTableData } from '../db-viewer/data/dataAccess';
import {
  getCurrentUser, getAccountOwner, getOwnerEntities, OwnerEntity,
} from '../bookkeeping/utils/ownerEntities';
import { addNotification } from '../../lib/notifications';
import './DeletedItemsTab.css';

// =============================================
// DMS → Reports → Deleted Items
// User-facing recovery for soft-deleted documents (is_deleted = 1). Scoped the
// SAME way as the Process Docs/Receipts document list: an account owner sees every
// deleted doc across their books (self + family + businesses); a regular user sees
// only their own. Restore flips is_deleted back to 0. This keeps recovery
// self-serve for users — Data & Backups stays admin-only.
// =============================================

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

interface DocRow {
  document_id: number;
  filename?: string;
  user_id?: number | null;
  owner_type?: string | null;
  owner_id?: number | null;
  dms_folders_id?: number | null;
  is_deleted?: number | string;
  updated_at?: string;
  created_at?: string;
  [k: string]: any;
}

interface DocOwnerRow {
  document_id: number;
  owner_type: string;
  owner_id: number;
  is_deleted?: number | string;
  is_active?: number | string;
}

// Primary-owner match — mirrors ProcessReceiptsTab's docMatchesEntity.
function docMatchesEntity(d: DocRow, e: OwnerEntity): boolean {
  if (e.owner_type === 'account_owner' || e.owner_type === 'family_member') {
    // eslint-disable-next-line eqeqeq
    return d.user_id == e.owner_id;
  }
  // eslint-disable-next-line eqeqeq
  return d.owner_type === e.owner_type && d.owner_id == e.owner_id;
}

function ownerNickname(ownerType: string | null | undefined, ownerId: number | null | undefined, users: any[], businesses: any[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businesses.find((b: any) => b.business_id == ownerId);
    return b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = users.find((u: any) => u.user_id == ownerId);
  return u ? String(u.nickname || '') : '';
}

function ownerName(ownerType: string | null | undefined, ownerId: number | null | undefined, users: any[], businesses: any[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businesses.find((b: any) => b.business_id == ownerId);
    return b ? (b.busi_legal_name || b.busi_operating_name || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = users.find((u: any) => u.user_id == ownerId);
  return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '' : '';
}

const DeletedItemsTab: React.FC = () => {
  const [documents, setDocuments] = useState<DocRow[]>([]);
  const [docOwners, setDocOwners] = useState<DocOwnerRow[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [msg, setMsg] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTable('dms_documents'),
        loadTable('users'),
        loadTable('user_businesses'),
        loadTable('dms_folders'),
        loadTable('dms_document_owners').catch(() => {}),
      ]);
    } catch { /* fall back to whatever cache holds */ }
    setDocuments(getTableData('dms_documents') as DocRow[]);
    setUsers(getTableData('users'));
    setBusinesses(getTableData('user_businesses'));
    setFolders(getTableData('dms_folders'));
    setDocOwners(getTableData('dms_document_owners') as DocOwnerRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const currentUser = useMemo(() => getCurrentUser(users), [users]);
  const accountOwner = useMemo(() => getAccountOwner(users), [users]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;
  const canPickOwners = !!currentUser && currentUser.owner_id == null;

  // Owner entities (Your Accounts / DMS scope — no client books here).
  const entities = useMemo(() => {
    if (!accountOwnerId) return [] as OwnerEntity[];
    return getOwnerEntities(accountOwnerId, users, businesses)
      .filter((e) => e.owner_type !== 'client_personal' && e.owner_type !== 'client_business');
  }, [accountOwnerId, users, businesses]);

  // A regular (non-owner) user is pinned to their own entity.
  const selfEntity = useMemo(() => {
    if (!currentUser) return null;
    return entities.find((e) =>
      // eslint-disable-next-line eqeqeq
      e.owner_id == currentUser.user_id && (e.owner_type === 'account_owner' || e.owner_type === 'family_member')) || null;
  }, [entities, currentUser]);

  // Shared-owner ("Also owned by") rows grouped by document.
  const sharedKeysByDoc = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const r of docOwners) {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.is_active == 0) continue;
      const set = m.get(r.document_id) || new Set<string>();
      set.add(`${r.owner_type}:${r.owner_id}`);
      m.set(r.document_id, set);
    }
    return m;
  }, [docOwners]);

  const scopeEntities = canPickOwners ? entities : (selfEntity ? [selfEntity] : []);

  const folderName = useCallback((id: number | null | undefined): string => {
    if (!id) return '';
    // eslint-disable-next-line eqeqeq
    const f = folders.find((f: any) => f.folder_id == id);
    return f ? (f.folder_name || '') : '';
  }, [folders]);

  // Deleted docs the current user is allowed to see, matching the doc-list scope.
  const deletedDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents
      // eslint-disable-next-line eqeqeq
      .filter((d) => d.is_deleted == 1)
      .filter((d) => {
        if (!scopeEntities.length) return false;
        if (scopeEntities.some((e) => docMatchesEntity(d, e))) return true;
        const shared = sharedKeysByDoc.get(d.document_id);
        if (shared && scopeEntities.some((e) => shared.has(`${e.owner_type}:${e.owner_id}`))) return true;
        return false;
      })
      .filter((d) => !q || (d.filename || '').toLowerCase().includes(q))
      .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
  }, [documents, scopeEntities, sharedKeysByDoc, search]);

  const handleRestore = useCallback(async (doc: DocRow) => {
    setRestoringId(doc.document_id);
    setMsg('');
    try {
      await saveRow('dms_documents', doc.document_id, { is_deleted: 0, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setRestoringId(null);
      setMsg(`Couldn't restore "${doc.filename}". Please try again.`);
      return;
    }
    setDocuments((prev) => prev.map((d) => d.document_id === doc.document_id ? { ...d, is_deleted: 0 } : d));
    // Shared-resource rule: a non-account-owner edit notifies the account owner.
    if (currentUser && accountOwnerId && currentUser.user_id !== accountOwnerId) {
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'document_restored',
        title: 'Document restored',
        message: `${currentUser.first_name} restored "${doc.filename}"`,
        actor_user_id: currentUser.user_id,
        actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
      });
    }
    setRestoringId(null);
    setMsg(`"${doc.filename}" restored — it's back in Process Docs / Receipts.`);
  }, [currentUser, accountOwnerId]);

  return (
    <div className="del-items">
      <div className="del-items-head">
        <div>
          <h2 className="del-items-title"><FiTrash2 size={18} /> Deleted Items</h2>
          <p className="del-items-sub">
            Documents you deleted are kept here (soft-deleted, not erased). Restore one to send it back to
            Process Docs / Receipts.
          </p>
        </div>
        <button className="del-items-refresh" onClick={fetchAll} disabled={loading} title="Refresh">
          <FiRefreshCw size={14} className={loading ? 'del-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="del-items-toolbar">
        <div className="del-items-search">
          <FiSearch size={14} />
          <input
            type="text"
            placeholder="Search deleted documents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="del-items-count">{deletedDocs.length} deleted</span>
      </div>

      {msg && <div className="del-items-msg">{msg}</div>}

      {loading ? (
        <div className="del-items-empty">Loading…</div>
      ) : deletedDocs.length === 0 ? (
        <div className="del-items-empty">
          {search ? 'No deleted documents match your search.' : 'No deleted documents. 🎉'}
        </div>
      ) : (
        <div className="del-items-table-wrap">
          <table className="del-items-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Owner</th>
                <th>Folder</th>
                <th>Deleted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deletedDocs.map((doc) => {
                const nick = ownerNickname(doc.owner_type, doc.owner_id, users, businesses)
                  // person docs may carry owner only via user_id (account_owner/family)
                  || (doc.user_id ? ownerNickname('account_owner', Number(doc.user_id), users, businesses) : '');
                const name = ownerName(doc.owner_type, doc.owner_id, users, businesses)
                  || (doc.user_id ? ownerName('account_owner', Number(doc.user_id), users, businesses) : '');
                const fld = folderName(doc.dms_folders_id);
                return (
                  <tr key={doc.document_id}>
                    <td className="del-items-fname">{doc.filename || `Document #${doc.document_id}`}</td>
                    <td>
                      {nick && <span className="del-items-nick">{nick}</span>}
                      <span className="del-items-owner">{name || '—'}</span>
                    </td>
                    <td>{fld || <span className="del-items-muted">Unfiled</span>}</td>
                    <td className="del-items-date">{(doc.updated_at || '').slice(0, 10) || '—'}</td>
                    <td className="del-items-actions">
                      <button
                        className="del-items-restore"
                        onClick={() => handleRestore(doc)}
                        disabled={restoringId === doc.document_id}
                        title="Restore this document"
                      >
                        <FiRotateCcw size={14} />
                        {restoringId === doc.document_id ? 'Restoring…' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeletedItemsTab;
